import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Users, UserPlus, RefreshCw, Send, KeyRound, Trash2, AlertTriangle, ChevronLeft, ChevronRight, X, Search } from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { db, appId, functions } from "../firebase/firebaseConfig.jsx";
import { collection, query, orderBy, getDocs, where, doc, deleteDoc, getDoc, updateDoc, addDoc } from "firebase/firestore";

const UserManagementPage = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [employmentFilter, setEmploymentFilter] = useState("all");
  const [todayFilter, setTodayFilter] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });
  const [deleting, setDeleting] = useState(false);

  const [passwordModal, setPasswordModal] = useState({ open: false, user: null });
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  
  // 계약 종료일 임박 사용자 메시지 전송 관련 상태
  const [selectedContractExpiryUsers, setSelectedContractExpiryUsers] = useState([]);
  const [sendingContractMessage, setSendingContractMessage] = useState(false);
  
  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [usersPerPage] = useState(15);
  
  // 통계 데이터 상태 (전체 사용자 기준)
  const [employmentStats, setEmploymentStats] = useState({
    employed: 0,
    contract_extension: 0,
    job_searching: 0,
    returned_myanmar: 0,
    terminated: 0,
    unknown: 0
  });
  const [todayJoined, setTodayJoined] = useState(0);

  // 초기 로딩 (통계 데이터)
  useEffect(() => {
    loadStatistics();
    // 백그라운드에서 고용현황 통계 업데이트
    setTimeout(() => {
      updateEmploymentStats();
    }, 1000);
  }, []);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim().toLowerCase());
      setCurrentPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // 사용자의 고용현황 컬렉션에서 최신 계약 정보 가져오기
  const getLatestEmploymentData = async (userId) => {
    try {
      const employmentsRef = collection(db, `artifacts/${appId}/users/${userId}/employments`);
      const employmentsQuery = query(employmentsRef, orderBy('contractStartDate', 'desc'));
      const snapshot = await getDocs(employmentsQuery);
      
      if (snapshot.empty) {
        return null;
      }
      
      // 가장 최근 고용현황 데이터 반환
      return snapshot.docs[0].data();
      } catch (error) {
      console.error(`사용자 ${userId} 고용현황 데이터 로드 실패:`, error);
        return null;
      }
  };

  // 고용현황 판단 함수
  const determineEmploymentStatus = async (user) => {
    // 고용현황 컬렉션에서 최신 계약 정보 가져오기
    const employmentData = await getLatestEmploymentData(user.id);
    
    // 🔥 계약 종료일을 먼저 확인하여 만료 여부 판단
    if (employmentData?.contractEndDate) {
      const today = new Date();
      const endDate = new Date(employmentData.contractEndDate);
      
      // 계약이 만료되었으면 무조건 'terminated' 반환 (명시적 설정보다 우선)
      if (endDate < today) {
        return 'terminated';
      }
    }
    
    // 계약이 유효한 경우에만 명시적으로 설정된 고용현황 사용
    if (user.employmentStatus && user.employmentStatus !== 'unknown') {
      return user.employmentStatus;
    }
    
    if (!employmentData) {
      return 'unknown'; // 고용현황 정보가 없으면 미분류
    }
    
    // 고용현황 컬렉션에서 가져온 상태가 있으면 사용
    if (employmentData.employmentStatus && employmentData.employmentStatus !== 'unknown') {
      return employmentData.employmentStatus;
    }
    
    // 계약 종료일이 있고 아직 유효한 경우
    if (employmentData.contractEndDate) {
      // 계약이 아직 유효함 - 고용중으로 간주
      return 'employed';
    }
    
    return 'unknown'; // 계약 정보가 없으면 미분류
  };

  // 통계 데이터 로딩 (전체 사용자 기준) - 단순화된 버전
  const loadStatistics = async () => {
    try {
      const usersRef = collection(db, `artifacts/${appId}/users`);
      const snapshot = await getDocs(usersRef);
      
      const allUsersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Admin 사용자 제외
      const filteredUsers = allUsersData.filter(user => 
        user.role !== 'admin' && 
        user.role !== 'administrator' &&
        !user.isAdmin
      );
      
      // 전체 사용자 수 설정
      setTotalUsers(filteredUsers.length);
      
      // 기본 통계 설정 (빠른 로딩을 위해)
      setEmploymentStats({
        employed: 0,
        contract_extension: 0,
        job_searching: 0,
        returned_myanmar: 0,
        terminated: 0,
        unknown: 0
      });
      
      // 오늘 가입자 카운트만 계산
      let todayJoinedCount = 0;
      const today = new Date();
      
      for (const user of filteredUsers) {
        const userDate = user.createdAt?.toDate?.() || new Date(user.createdAt);
        if (userDate.toDateString() === today.toDateString()) {
          todayJoinedCount++;
        }
      }
      
      setTodayJoined(todayJoinedCount);
      
      console.log('📊 기본 통계 데이터 로드 완료:', {
        totalUsers: filteredUsers.length,
        todayJoined: todayJoinedCount
      });
      
    } catch (error) {
      console.error('통계 데이터 로드 실패:', error);
    } finally {
      setLoading(false); // 로딩 상태 해제
    }
  };

  // 고용현황 통계 업데이트 (백그라운드에서 실행)
  const updateEmploymentStats = async () => {
    try {
      const usersRef = collection(db, `artifacts/${appId}/users`);
          const snapshot = await getDocs(usersRef);
          
      const allUsersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Admin 사용자 제외
      const filteredUsers = allUsersData.filter(user => 
        user.role !== 'admin' && 
        user.role !== 'administrator' &&
        !user.isAdmin
      );
      
      // 고용현황 통계 계산
      const stats = {
        employed: 0,
        contract_extension: 0,
        job_searching: 0,
        returned_myanmar: 0,
        terminated: 0,
        unknown: 0
      };
      
      // 각 사용자의 고용현황 판단 (비동기 처리)
      const promises = filteredUsers.map(async (user) => {
        const employmentStatus = await determineEmploymentStatus(user);
        return employmentStatus;
      });
      
      const employmentStatuses = await Promise.all(promises);
      
      // 통계 집계
      employmentStatuses.forEach(status => {
        if (stats.hasOwnProperty(status)) {
          stats[status]++;
        } else {
          stats.unknown++;
        }
      });
      
      setEmploymentStats(stats);
      
      console.log('📊 고용현황 통계 업데이트 완료:', stats);
      
        } catch (error) {
      console.error('고용현황 통계 업데이트 실패:', error);
    }
  };

  const loadUsers = async () => {
    try {
      setTableLoading(true);
      
      // 사용자 데이터 조회 (페이지네이션 적용)
      const usersRef = collection(db, `artifacts/${appId}/users`);
      const usersQuery = query(usersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(usersQuery);
      
      const allUsersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
        
      // Admin 사용자 제외
      const filteredUsers = allUsersData.filter(user => 
        user.role !== 'admin' && 
        user.role !== 'administrator' &&
        !user.isAdmin
      );
      
      // 모든 사용자의 고용현황 데이터를 먼저 가져오기
      const usersWithEmploymentData = await Promise.all(
        filteredUsers.map(async (user) => {
          const originalStatus = user.employmentStatus;
          const determinedStatus = await determineEmploymentStatus(user);
          
          // 고용현황 컬렉션에서 최신 데이터 가져오기
          const employmentData = await getLatestEmploymentData(user.id);
        
            return {
              ...user,
            employmentStatus: determinedStatus,
            employmentData: employmentData
          };
        })
      );
      
      // 계약 종료일 기준으로 전체 사용자 정렬
      const sortedAllUsers = sortUsersByContractEndDate(usersWithEmploymentData);
      
      setAllUsers(sortedAllUsers);
    } catch (error) {
      console.error('사용자 데이터 로드 실패:', error);
    } finally {
      setTableLoading(false);
    }
  };

  // 사용자 이름 가져오기 (여러 필드 확인)
  const getUserDisplayName = (user) => {
    // 각 필드를 개별적으로 확인하여 더 안전하게 처리
    if (user.fullName && user.fullName.trim()) {
      return user.fullName.trim();
    }
    
    if (user.displayName && user.displayName.trim()) {
      return user.displayName.trim();
    }
    
    if (user.name && user.name.trim()) {
      return user.name.trim();
    }
    
    if (user.realName && user.realName.trim()) {
      return user.realName.trim();
    }
    
    if (user.username && user.username.trim()) {
      return user.username.trim();
    }
    
    if (user.nickName && user.nickName.trim()) {
      return user.nickName.trim();
    }
    
    if (user.nickname && user.nickname.trim()) {
      return user.nickname.trim();
    }
    
    if (user.user_name && user.user_name.trim()) {
      return user.user_name.trim();
    }
    
    // firstName과 lastName 조합
    if (user.firstName || user.lastName) {
      const firstName = user.firstName ? user.firstName.trim() : '';
      const lastName = user.lastName ? user.lastName.trim() : '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) {
        return fullName;
      }
    }
    
    // 이메일에서 사용자명 추출
    if (user.email) {
      const emailName = user.email.split('@')[0];
      if (emailName && emailName.trim()) {
        return emailName.trim();
      }
    }
    
    // 모든 방법이 실패하면 기본값
    return '이름 없음';
  };

  // 계약 종료일 기준으로 사용자 정렬
  const sortUsersByContractEndDate = (users) => {
    return users.sort((a, b) => {
      const aEndDate = a.employmentData?.contractEndDate;
      const bEndDate = b.employmentData?.contractEndDate;
      
      // 계약 종료일이 없는 사용자는 맨 뒤로
      if (!aEndDate && !bEndDate) return 0;
      if (!aEndDate) return 1;
      if (!bEndDate) return -1;
      
      // 계약 종료일이 가까운 순서로 정렬 (오름차순)
      const aDate = new Date(aEndDate);
      const bDate = new Date(bEndDate);
      
      return aDate - bDate;
    });
  };

  // 사용자 삭제 함수
  const deleteUser = async (userId) => {
    try {
      setDeleting(true);
      
      // 1. 사용자 기본 데이터 삭제
      await deleteDoc(doc(db, `artifacts/${appId}/users`, userId));
      
      // 2. 사용자 관련 서브컬렉션들 삭제
      const subCollections = [
        'employments',
        'remittances', 
        'notifications',
        'consultations',
        'kyc',
        'loans',
        'messages',
        'community_posts',
        'community_comments',
        'settings',
        'activity_logs'
      ];
      
      for (const subCollection of subCollections) {
        try {
          const subRef = collection(db, `artifacts/${appId}/users/${userId}/${subCollection}`);
          const subSnapshot = await getDocs(subRef);
          
          // 서브컬렉션의 모든 문서 삭제
          const deletePromises = subSnapshot.docs.map(doc => deleteDoc(doc.ref));
          await Promise.all(deletePromises);
        } catch (error) {
          console.warn(`서브컬렉션 ${subCollection} 삭제 중 오류:`, error);
        }
      }
      
      // 3. 다른 컬렉션에서 해당 사용자 참조 삭제
      try {
        // 채팅방에서 사용자 참조 삭제
        const chatRoomsRef = collection(db, `artifacts/${appId}/chatRooms`);
        const chatRoomsSnapshot = await getDocs(chatRoomsRef);
        
        for (const chatRoomDoc of chatRoomsSnapshot.docs) {
          const chatRoomData = chatRoomDoc.data();
          if (chatRoomData.members && chatRoomData.members.includes(userId)) {
            const updatedMembers = chatRoomData.members.filter(memberId => memberId !== userId);
            await updateDoc(chatRoomDoc.ref, { members: updatedMembers });
          }
        }
        
        // 커뮤니티 게시글에서 사용자 참조 삭제
        const communityRef = collection(db, `artifacts/${appId}/community`);
        const communitySnapshot = await getDocs(communityRef);
        
        for (const postDoc of communitySnapshot.docs) {
          const postData = postDoc.data();
          if (postData.authorId === userId) {
            await deleteDoc(postDoc.ref);
          }
        }
        
      } catch (error) {
        console.warn('관련 데이터 정리 중 오류:', error);
      }
      
      // 4. 로컬 상태에서 사용자 제거
      setAllUsers(prev => prev.filter(user => user.id !== userId));
      setTotalUsers(prev => Math.max(0, prev - 1));
      
      // 5. 모달 닫기
      setDeleteModal({ open: false, user: null });
      
      alert('사용자와 관련된 모든 데이터가 성공적으로 삭제되었습니다.');
      
    } catch (error) {
      console.error('사용자 삭제 실패:', error);
      alert('사용자 삭제 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  // 삭제 확인 모달 열기
  const openDeleteModal = (user) => {
    setDeleteModal({ open: true, user });
  };

  // 삭제 확인 모달 닫기
  const closeDeleteModal = () => {
    setDeleteModal({ open: false, user: null });
  };

  const openPasswordModal = (user) => {
    setPasswordError("");
    setNewPassword("");
    setConfirmNewPassword("");
    setPasswordModal({ open: true, user });
  };

  const closePasswordModal = () => {
    if (passwordSaving) return;
    setPasswordModal({ open: false, user: null });
    setNewPassword("");
    setConfirmNewPassword("");
    setPasswordError("");
  };

  const handleSavePassword = async () => {
    setPasswordError("");
    const u = passwordModal.user;
    if (!u?.id) return;
    const pw = newPassword.trim();
    if (!pw) {
      setPasswordError("새 비밀번호를 입력하세요.");
      return;
    }
    if (pw.length < 8) {
      setPasswordError("비밀번호는 8자 이상이어야 합니다.");
      return;
    }
    if (!/[A-Za-z]/.test(pw) || !/[0-9]/.test(pw)) {
      setPasswordError("비밀번호는 영문과 숫자를 각각 1자 이상 포함해야 합니다.");
      return;
    }
    if (pw !== confirmNewPassword.trim()) {
      setPasswordError("비밀번호 확인이 일치하지 않습니다.");
      return;
    }
    setPasswordSaving(true);
    try {
      const fn = httpsCallable(functions, "adminResetUserPassword");
      const emailNorm = (u.email && String(u.email).trim()) ? String(u.email).trim().toLowerCase() : '';
      const result = await fn({
        uid: u.id,
        ...(emailNorm ? { email: emailNorm } : {}),
        newPassword: pw,
      });
      setPasswordModal({ open: false, user: null });
      setNewPassword("");
      setConfirmNewPassword("");
      setPasswordError("");
      const appliedAt = result?.data?.appliedAt
        ? new Date(result.data.appliedAt).toLocaleString('ko-KR')
        : '즉시';
      alert(`비밀번호가 변경되었습니다. (${appliedAt}부터 새 비밀번호로 로그인 가능)`);
    } catch (e) {
      const code = e?.code;
      const msg = e?.message || String(e);
      if (code === "functions/permission-denied") {
        setPasswordError("관리자 권한이 없습니다. Functions 배포 및 계정 role을 확인하세요.");
      } else if (code === "functions/not-found") {
        setPasswordError("Firebase Auth에 해당 사용자가 없습니다(uid 불일치 또는 미가입).");
      } else if (code === "functions/unauthenticated") {
        setPasswordError("다시 로그인한 뒤 시도하세요.");
      } else if (code === "functions/invalid-argument") {
        setPasswordError(msg.replace(/^.*:\s*/, "") || "입력값이 올바르지 않습니다.");
      } else {
        setPasswordError(msg.replace(/^FirebaseError:\s*/i, ""));
      }
    } finally {
      setPasswordSaving(false);
    }
  };

  // 페이지 변경 함수
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // 필터 변경 시 첫 페이지로 이동
  const handleFilterChange = (filter) => {
    setEmploymentFilter(filter);
    setCurrentPage(1);
  };

  // 계약 종료일이 30일 미만 남은 사용자 필터링 (또는 만료된 사용자)
  const getContractExpiryUsers = () => {
    // filteredUsers는 렌더링 시점에 정의되므로, users 상태를 직접 사용
    return users.filter(user => {
      if (!user.employmentData?.contractEndDate) return false;
      
      const endDate = new Date(user.employmentData.contractEndDate);
      const today = new Date();
      const daysUntilExpiry = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
      
      // 계약이 만료되었거나 30일 이내에 만료되는 사용자
      return daysUntilExpiry <= 30;
    });
  };

  // 계약 종료일 임박 사용자 선택/해제
  const handleSelectContractExpiryUser = (userId) => {
    setSelectedContractExpiryUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // 모든 계약 종료일 임박 사용자 선택/해제
  const handleSelectAllContractExpiryUsers = () => {
    const expiryUsers = getContractExpiryUsers();
    if (selectedContractExpiryUsers.length === expiryUsers.length) {
      setSelectedContractExpiryUsers([]);
    } else {
      setSelectedContractExpiryUsers(expiryUsers.map(user => user.id));
    }
  };

  // 계약 종료일 알림 메시지 전송
  const handleSendContractExpiryMessage = async () => {
    if (selectedContractExpiryUsers.length === 0) {
      alert('계약 종료일이 임박한 사용자를 선택해주세요.');
      return;
    }

    setSendingContractMessage(true);
    
    try {
      console.log('🚀 계약 종료일 알림 메시지 전송 시작:', {
        selectedUsers: selectedContractExpiryUsers.length
      });

      // 선택된 사용자들의 정보 가져오기
      const selectedUsers = allUsers.filter(user => 
        selectedContractExpiryUsers.includes(user.id)
      );

      // 각 사용자에게 개별 메시지 전송
      for (const user of selectedUsers) {
        const endDate = new Date(user.employmentData.contractEndDate);
        const today = new Date();
        const daysUntilExpiry = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
        
        // 미얀마어 메시지 생성
        const myanmarMessage = `သင့်အလုပ်သက်တမ်းက ${daysUntilExpiry} ရက်သာ ကျန်ရှိပါပြီ။ အလုပ်အခြေအနေကို အပ်ဒိတ်လုပ်ပေးပါ။ သက်တမ်းတိုးခြင်း၊ မြန်မာပြည်ပြန်ခြင်း၊ သို့မဟုတ် အလုပ်အသစ်ရှာခြင်းတို့ကို စဉ်းစားပါ။`;
        
        // 한국어 메시지 (백업용)
        const koreanMessage = `계약 종료일이 ${daysUntilExpiry}일 남았습니다. 고용현황을 업데이트 해주세요. 계약 연장, 미얀마 귀국, 또는 새로운 일자리 찾기를 고려해주세요.`;

        // 알림 데이터 구성 (프론트엔드 호환 구조)
        const notificationData = {
          title: 'Contract Expiry Reminder',
          message: myanmarMessage,
          body: myanmarMessage,
          type: 'contract_expiry_reminder',
          language: 'myanmar',
          sender: 'admin',
          targetType: 'specific_users',
          targetUsers: [user.id],
          contractEndDate: user.employmentData.contractEndDate,
          daysUntilExpiry: daysUntilExpiry,
          createdAt: new Date(),
          read: false,
          source: 'admin'
        };

        // 메인 알림 컬렉션에 저장
        const mainNotificationRef = await addDoc(collection(db, `artifacts/${appId}/notifications`), notificationData);
        
        // 사용자별 알림 컬렉션에도 저장 (프론트엔드 호환성)
        const userNotificationData = {
          ...notificationData,
          notificationId: mainNotificationRef.id
        };
        
        await addDoc(collection(db, `artifacts/${appId}/users/${user.id}/notifications`), userNotificationData);
        
        console.log(`✅ 사용자 ${getUserDisplayName(user)}에게 계약 종료일 알림 전송 완료:`, {
          userId: user.id,
          mainNotificationId: mainNotificationRef.id,
          message: myanmarMessage,
          daysUntilExpiry: daysUntilExpiry
        });
      }

      alert(`계약 종료일 알림을 ${selectedContractExpiryUsers.length}명에게 전송했습니다.`);
      setSelectedContractExpiryUsers([]);
      
        } catch (error) {
      console.error('계약 종료일 알림 전송 실패:', error);
      console.error('에러 상세:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      alert('계약 종료일 알림 전송 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setSendingContractMessage(false);
    }
  };

  // 오늘 가입자 필터 토글
  const handleTodayFilterToggle = () => {
    setTodayFilter(!todayFilter);
    setCurrentPage(1);
  };

  const matchesSearch = (user) => {
    if (!searchQuery) return true;
    const fields = [
      getUserDisplayName(user),
      user.fullName,
      user.displayName,
      user.name,
      user.username,
      user.nickname,
      user.nickName,
      user.email,
      user.id,
      user.phone,
      user.phoneNumber,
    ];
    return fields.some((v) => v && String(v).toLowerCase().includes(searchQuery));
  };

  // 전체 사용자 기준 필터링 (고용현황 · 오늘 가입 · 검색)
  const matchedUsers = useMemo(() => {
    const todayStr = new Date().toDateString();
    return allUsers.filter((user) => {
      if (employmentFilter !== "all" && user.employmentStatus !== employmentFilter) return false;
      if (todayFilter) {
        const userDate = user.createdAt?.toDate?.() || new Date(user.createdAt);
        if (userDate.toDateString() !== todayStr) return false;
      }
      return matchesSearch(user);
    });
  }, [allUsers, employmentFilter, todayFilter, searchQuery]);

  const totalPages = Math.ceil(matchedUsers.length / usersPerPage);
  const users = useMemo(
    () => matchedUsers.slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage),
    [matchedUsers, currentPage, usersPerPage]
  );
  const filteredUsers = users;
  const isFiltering = employmentFilter !== "all" || todayFilter || !!searchQuery;

  const STATUS_META = {
    employed: { label: 'Employed', chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500' },
    contract_extension: { label: 'Contract Extension', chip: 'bg-blue-50 text-blue-700 ring-blue-200', dot: 'bg-blue-500' },
    job_searching: { label: 'Job Searching', chip: 'bg-amber-50 text-amber-700 ring-amber-200', dot: 'bg-amber-500' },
    returned_myanmar: { label: 'Returned to Myanmar', chip: 'bg-purple-50 text-purple-700 ring-purple-200', dot: 'bg-purple-500' },
    terminated: { label: 'Terminated', chip: 'bg-red-50 text-red-700 ring-red-200', dot: 'bg-red-500' },
    unknown: { label: 'Unknown', chip: 'bg-slate-100 text-slate-600 ring-slate-200', dot: 'bg-slate-400' },
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const d = value?.toDate?.() || new Date(value);
    return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
  };

  const daysUntil = (value) => {
    if (!value) return null;
    return Math.ceil((new Date(value) - new Date()) / (1000 * 60 * 60 * 24));
  };

  const expiryUsers = getContractExpiryUsers();
  const allExpirySelected = expiryUsers.length > 0 && selectedContractExpiryUsers.length === expiryUsers.length;
  const someExpirySelected = selectedContractExpiryUsers.length > 0 && !allExpirySelected;
  const pageStart = matchedUsers.length === 0 ? 0 : (currentPage - 1) * usersPerPage + 1;
  const pageEnd = Math.min(currentPage * usersPerPage, matchedUsers.length);

  const pageNumbers = Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
    if (totalPages <= 5 || currentPage <= 3) return i + 1;
    if (currentPage >= totalPages - 2) return totalPages - 4 + i;
    return currentPage - 2 + i;
  });

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Loading user data...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-3">
      <div className="space-y-3">
        {/* Header + key stats */}
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900">
                <Users className="h-4 w-4 text-white" />
              </div>
              <div>
                <h1 className="text-[15px] font-semibold leading-tight text-slate-900">User Management</h1>
                <p className="text-[11px] leading-tight text-slate-500">Employment status monitoring & account management</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-indigo-50 text-indigo-600">
                  <Users className="h-3.5 w-3.5" />
                </span>
                <span>
                  <span className="block text-[10px] leading-tight text-slate-500">Total Users</span>
                  <span className="block text-sm font-bold leading-tight text-slate-900 tabular-nums">{totalUsers.toLocaleString()}</span>
                </span>
              </div>
              <button
                type="button"
                onClick={handleTodayFilterToggle}
                className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left transition-colors ${
                  todayFilter ? 'border-purple-300 bg-purple-50' : 'border-slate-200 hover:border-purple-200'
                }`}
                title={todayFilter ? 'Show all users' : "Show today's new users"}
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-purple-50 text-purple-600">
                  <UserPlus className="h-3.5 w-3.5" />
                </span>
                <span>
                  <span className="block text-[10px] leading-tight text-slate-500">Today's New</span>
                  <span className="block text-sm font-bold leading-tight text-slate-900 tabular-nums">{todayJoined}</span>
                </span>
              </button>
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-orange-50 text-orange-600">
                  <AlertTriangle className="h-3.5 w-3.5" />
                </span>
                <span>
                  <span className="block text-[10px] leading-tight text-slate-500">Expiring (page)</span>
                  <span className="block text-sm font-bold leading-tight text-slate-900 tabular-nums">{expiryUsers.length}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Employment status distribution (click to filter) */}
          <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-slate-100 pt-2.5">
            <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Employment</span>
            <button
              type="button"
              onClick={() => handleFilterChange('all')}
              className={`inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium ring-1 transition-colors ${
                employmentFilter === 'all' ? 'bg-slate-900 text-white ring-slate-900' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
              }`}
            >
              All
            </button>
            {Object.entries(STATUS_META).map(([key, meta]) => (
              <button
                key={key}
                type="button"
                onClick={() => handleFilterChange(employmentFilter === key ? 'all' : key)}
                className={`inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium ring-1 transition-colors ${
                  employmentFilter === key ? 'bg-slate-900 text-white ring-slate-900' : `${meta.chip} hover:brightness-95`
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${employmentFilter === key ? 'bg-white' : meta.dot}`} />
                {meta.label}
                <span className="font-bold tabular-nums">{employmentStats[key] || 0}</span>
              </button>
            ))}
          </div>
        </div>

        {/* User table */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
            <h2 className="text-[13px] font-semibold text-slate-800">
              Users <span className="font-normal text-slate-500">({matchedUsers.length.toLocaleString()})</span>
            </h2>
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="이름, 아이디(이메일), UID로 검색..."
                className="h-8 w-full rounded-md border border-slate-200 bg-white pl-8 pr-8 text-[13px] focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput("")}
                  className="absolute right-2 top-1/2 flex !h-5 !min-h-0 !w-5 !min-w-0 -translate-y-1/2 items-center justify-center !p-0 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {todayFilter && (
              <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 py-0.5 pl-2 pr-1 text-[11px] font-medium text-purple-700">
                Today's new users
                <button type="button" onClick={handleTodayFilterToggle} className="rounded-full p-0.5 hover:bg-purple-200">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            <div className="ml-auto flex items-center gap-2">
              {selectedContractExpiryUsers.length > 0 && (
                <button
                  type="button"
                  onClick={handleSendContractExpiryMessage}
                  disabled={sendingContractMessage}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md bg-orange-600 px-3 text-xs font-semibold text-white hover:bg-orange-700 disabled:opacity-50"
                >
                  <Send className="h-3.5 w-3.5" />
                  {sendingContractMessage ? 'Sending...' : `Send Contract Reminder (${selectedContractExpiryUsers.length})`}
                </button>
              )}
              <button
                type="button"
                onClick={loadUsers}
                disabled={tableLoading}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${tableLoading ? 'animate-spin' : ''}`} />
                {tableLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            {tableLoading ? (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Loading users...
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10">
                <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                  <Users className="h-5 w-5 text-slate-400" />
                </div>
                <p className="text-sm font-medium text-slate-700">No users found</p>
                <p className="text-xs text-slate-500">
                  {searchQuery
                    ? `"${searchInput.trim()}"에 해당하는 사용자가 없습니다.`
                    : isFiltering
                      ? 'No users match the current filter.'
                      : 'No users have been registered yet.'}
                </p>
              </div>
            ) : (
              <table className="w-full min-w-[960px] text-left">
                <thead className="border-b border-slate-200 bg-white">
                  <tr className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    <th className="w-10 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={allExpirySelected}
                        ref={(el) => { if (el) el.indeterminate = someExpirySelected; }}
                        onChange={handleSelectAllContractExpiryUsers}
                        disabled={expiryUsers.length === 0}
                        title="Select all users with contracts expiring within 30 days"
                        className="h-4 w-4 cursor-pointer accent-orange-600 disabled:cursor-not-allowed disabled:opacity-40"
                      />
                    </th>
                    <th className="px-3 py-2">User</th>
                    <th className="px-3 py-2">Joined</th>
                    <th className="px-3 py-2">Company</th>
                    <th className="px-3 py-2">Contract</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredUsers.map((user) => {
                    const days = daysUntil(user.employmentData?.contractEndDate);
                    const isContractExpiring = days !== null && days <= 30 && days >= 0;
                    const isSelected = selectedContractExpiryUsers.includes(user.id);
                    const meta = STATUS_META[user.employmentStatus] || STATUS_META.unknown;
                    const name = getUserDisplayName(user);

                    return (
                      <tr key={user.id} className={`text-[13px] ${isSelected ? 'bg-orange-50/60' : 'hover:bg-slate-50'}`}>
                        <td className="px-3 py-2">
                          {isContractExpiring ? (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleSelectContractExpiryUser(user.id)}
                              className="h-4 w-4 cursor-pointer accent-orange-600"
                            />
                          ) : (
                            <span className="pl-1 text-slate-300">–</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2.5">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600">
                              {(name || '?').charAt(0).toUpperCase()}
                            </span>
                            <div className="min-w-0">
                              <div className="max-w-[220px] truncate font-medium text-slate-900">{name}</div>
                              <div className="max-w-[220px] truncate text-[11px] text-slate-500">{user.email || '-'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-slate-600">{formatDate(user.createdAt)}</td>
                        <td className="px-3 py-2">
                          <div className="max-w-[180px] truncate text-slate-700">{user.employmentData?.companyName || '-'}</div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">
                          {user.employmentData?.contractStartDate || user.employmentData?.contractEndDate ? (
                            <div className="flex items-center gap-1.5">
                              <span className="text-slate-600">
                                {formatDate(user.employmentData?.contractStartDate)}
                                <span className="mx-1 text-slate-300">→</span>
                                <span className="font-medium text-slate-800">{formatDate(user.employmentData?.contractEndDate)}</span>
                              </span>
                              {isContractExpiring && (
                                <span className="rounded-full bg-orange-100 px-1.5 py-px text-[10px] font-semibold text-orange-700">D-{days}</span>
                              )}
                              {days !== null && days < 0 && (
                                <span className="rounded-full bg-red-100 px-1.5 py-px text-[10px] font-semibold text-red-700">만료됨</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${meta.chip}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                            {meta.label}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => openPasswordModal(user)}
                              className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-[11px] font-semibold text-indigo-600 hover:bg-indigo-50"
                              title="비밀번호 변경"
                            >
                              <KeyRound className="h-3.5 w-3.5" />
                              비밀번호
                            </button>
                            <button
                              type="button"
                              onClick={() => openDeleteModal(user)}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600"
                              title="Delete user"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 px-3 py-2">
              <span className="text-[11px] text-slate-500">
                Showing {pageStart}-{pageEnd} of {matchedUsers.length.toLocaleString()} users
                {isFiltering && <span className="text-slate-400"> (filtered from {allUsers.length.toLocaleString()})</span>}
              </span>
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  {pageNumbers.map((pageNum) => (
                    <button
                      key={pageNum}
                      type="button"
                      onClick={() => handlePageChange(pageNum)}
                      className={`h-7 min-w-[28px] rounded-md px-2 text-xs font-semibold ${
                        currentPage === pageNum ? 'bg-slate-900 text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 비밀번호 변경 모달 */}
        {passwordModal.open && passwordModal.user && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]">
            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.15 }}
              className="w-full max-w-md overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
            >
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                    <KeyRound className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-[15px] font-semibold leading-tight text-slate-900">사용자 비밀번호 변경</h3>
                    <p className="truncate text-[11px] leading-tight text-slate-500">
                      {getUserDisplayName(passwordModal.user)} · {passwordModal.user.email || passwordModal.user.id}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closePasswordModal}
                  disabled={passwordSaving}
                  className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-3 px-4 py-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-700">새 비밀번호 (8자 이상, 영문+숫자)</label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px] focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-700">새 비밀번호 확인</label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    className="h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px] focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                  />
                </div>
                {passwordError && (
                  <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" role="alert">
                    {passwordError}
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
                <button
                  type="button"
                  onClick={closePasswordModal}
                  disabled={passwordSaving}
                  className="h-9 rounded-lg bg-slate-100 px-4 text-xs font-semibold text-slate-700 hover:bg-slate-200 disabled:opacity-50"
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={handleSavePassword}
                  disabled={passwordSaving}
                  className="h-9 rounded-lg bg-slate-900 px-4 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {passwordSaving ? '저장 중...' : '저장'}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteModal.open && deleteModal.user && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]">
            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.15 }}
              className="w-full max-w-md overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
            >
              <div className="flex items-start gap-3 px-4 pt-4">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                  <AlertTriangle className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-[15px] font-semibold text-slate-900">Confirm User Deletion</h3>
                  <p className="mt-0.5 text-[13px] text-slate-600">
                    Delete <strong className="text-slate-900">{getUserDisplayName(deleteModal.user)}</strong>
                    {deleteModal.user.email ? <span className="text-slate-500"> ({deleteModal.user.email})</span> : null}?
                  </p>
                </div>
              </div>
              <div className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                <p className="text-xs font-semibold text-red-700">This action cannot be undone. The following data will be deleted:</p>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {['Basic info', 'Employment', 'Remittances', 'Notifications', 'Consultations', 'KYC', 'Loans', 'Messages', 'Community'].map((item) => (
                    <span key={item} className="rounded bg-white px-1.5 py-px text-[10px] font-medium text-red-700 ring-1 ring-red-200">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <div className="mt-3 flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  disabled={deleting}
                  className="h-9 rounded-lg bg-slate-100 px-4 text-xs font-semibold text-slate-700 hover:bg-slate-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => deleteUser(deleteModal.user.id)}
                  disabled={deleting}
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-red-600 px-4 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagementPage;